import { ASSET_MANIFEST } from '../config/assets';
import { AdService, DomAdView, NoOpAdAdapter } from '../infrastructure/ads/ad-service';
import { ContentLoader, fetchAssetProbe } from '../infrastructure/content/content-loader';
import { createMvpContentRegistry } from '../infrastructure/content/content-registry';
import type { AssetProbe } from '../infrastructure/content/content-loader';
import { readFeatureConfig } from '../infrastructure/config/feature-config';
import type { FeatureConfig } from '../infrastructure/config/feature-config';
import { FatalErrorController } from '../infrastructure/errors/fatal-error-ui';
import { ConsoleLogSink, Logger } from '../infrastructure/logging/logger';
import { SettingsService } from '../infrastructure/settings/settings-service';
import { BrowserStorageAdapter } from '../infrastructure/storage/storage-adapter';

export interface Phase6Services {
  readonly logger: Logger; readonly settings: SettingsService; readonly content: ContentLoader;
  readonly ads: AdService; readonly fatal: FatalErrorController; readonly featureConfig: FeatureConfig;
}

let services: Phase6Services | null = null;

export const initializePhase6Services = (gameElement: HTMLElement, adElement: HTMLElement, fatalElement: HTMLElement): Phase6Services => {
  const bootstrapSink = new ConsoleLogSink();
  const bootstrapLogger = new Logger(bootstrapSink, import.meta.env.PROD ? 'WARN' : 'DEBUG');
  const featureConfig = readFeatureConfig({
    adsEnabled: import.meta.env.VITE_ADS_ENABLED,
    debugLoggingEnabled: import.meta.env.VITE_DEBUG_LOGGING_ENABLED,
  }, import.meta.env.PROD, bootstrapLogger);
  const logger = new Logger(bootstrapSink, featureConfig.debugLoggingEnabled ? 'DEBUG' : 'WARN');
  const settings = new SettingsService(new BrowserStorageAdapter(), logger);
  let failedFixture = false;
  const probe: AssetProbe = async (asset) => {
    if (globalThis.__officeClashTestFixture === 'loading-fail-once' && !failedFixture && asset.required) {
      failedFixture = true; throw new Error('Controlled required asset failure.');
    }
    await fetchAssetProbe(asset);
  };
  const content = new ContentLoader(() => createMvpContentRegistry([
    { id: 'character-atlas', url: ASSET_MANIFEST.character_poc_neutral, required: true, kind: 'TEXTURE' },
    { id: 'effects-ui-atlas', url: ASSET_MANIFEST.effects_and_ui_shared_atlas, required: true, kind: 'TEXTURE' },
  ]), probe, logger);
  const ads = new AdService(new NoOpAdAdapter(), new DomAdView(adElement), logger, featureConfig.adsEnabled);
  const fatal = new FatalErrorController(gameElement, fatalElement);
  services = Object.freeze({ logger, settings, content, ads, fatal, featureConfig });
  void ads.initialize(); return services;
};

export const getPhase6Services = (): Phase6Services => {
  if (services === null) throw new Error('Phase 6 services are not initialized.'); return services;
};
